import type { Ref } from 'vue'
import { reactive } from 'vue'
import { msg } from '@/plugins/message'

/**
 * 预置导出数据类型文案，页面可通过 `dataLabel` 传入键名以统一口径。
 */
export const EXPORT_DATA_LABELS = {
  userList: '用户列表',
  personnelList: '黔籍在外人员',
  enterprise: '黔籍在外企业',
  securityLog: '安全日志',
  userAgreement: '用户协议',
  country: '国别数据',
  agency: '驻外机构',
  emergencyCase: '应急案件',
  evaluationReport: '评价报告',
  systemConfig: '系统配置',
} as const

export type ExportDataKind = keyof typeof EXPORT_DATA_LABELS

/** 人员 / 企业 / 驻外机构列表导出上限（与后端 OverseasExportLimits.MAX_ROWS 一致） */
export const OVERSEAS_LIST_EXPORT_MAX_ROWS = 5000

function formatCount(n: number): string {
  return n.toLocaleString('zh-CN')
}

function buildExportLimitHint(matchedCount: number | undefined, maxRows: number): string {
  const maxText = formatCount(maxRows)
  if (matchedCount != null && matchedCount > maxRows) {
    return `单次最多导出 ${maxText} 条。当前筛选约 ${formatCount(matchedCount)} 条，将只导出前 ${maxText} 条，请缩小筛选条件后分批导出。`
  }
  return `单次最多导出 ${maxText} 条。`
}

export function resolveExportLabel(dataLabel: string | ExportDataKind): string {
  if (dataLabel in EXPORT_DATA_LABELS) {
    return EXPORT_DATA_LABELS[dataLabel as ExportDataKind]
  }
  return dataLabel
}

export interface ExportConfirmCheckbox {
  /** 复选框文案 */
  label: string
  /** 默认是否勾选；不传为 false */
  defaultChecked?: boolean
}

export interface ExportConfirmOptions {
  /** 导出数据类型：预置键（EXPORT_DATA_LABELS）或任意说明文案 */
  dataLabel: string | ExportDataKind
  title?: string
  /** 完全自定义确认内容；不传则根据 dataLabel 生成 */
  content?: string
  /**
   * 确认框内可选复选框（如是否解密敏感字段）。
   * 勾选状态通过 exportFn 参数传入。
   */
  checkbox?: ExportConfirmCheckbox
  exportFn: (ctx?: { checked: boolean }) => Promise<void>
  successMessage?: string
  failMessage?: string
  /** 为 false 时不弹出「导出成功」（如仅提示待对接） */
  showSuccessMessage?: boolean
  loading?: Ref<boolean>
  okText?: string
  /** 单次导出上限；传入后确认文案会提示最多导出条数 */
  maxRows?: number
  /** 当前筛选命中条数（通常用列表分页 total） */
  matchedCount?: number
}

type ResolveFn = (ok: boolean) => void

let pending: {
  options: ExportConfirmOptions
  resolve: ResolveFn
} | null = null

export const exportConfirmUi = reactive({
  open: false,
  title: '确认导出',
  label: '',
  meta: '',
  extra: '',
  limitHint: '',
  checkboxLabel: '',
  checked: false,
  okText: '确定导出',
  submitting: false,
})

function resetUi() {
  exportConfirmUi.open = false
  exportConfirmUi.submitting = false
  exportConfirmUi.extra = ''
  exportConfirmUi.limitHint = ''
  exportConfirmUi.checkboxLabel = ''
  exportConfirmUi.checked = false
}

export function cancelExportConfirm() {
  if (exportConfirmUi.submitting) return
  pending?.resolve(false)
  pending = null
  resetUi()
}

export async function confirmExportConfirm() {
  if (!pending || exportConfirmUi.submitting) return
  const { options, resolve } = pending
  exportConfirmUi.submitting = true
  if (options.loading) options.loading.value = true
  try {
    await options.exportFn(options.checkbox ? { checked: exportConfirmUi.checked } : undefined)
    if (options.showSuccessMessage !== false) {
      msg.success(options.successMessage ?? '导出成功')
    }
    pending = null
    resetUi()
    resolve(true)
  } catch {
    // 失败提示由 request 拦截器统一处理，避免叠 toast
  } finally {
    exportConfirmUi.submitting = false
    if (options.loading) options.loading.value = false
  }
}

export function useExportConfirm() {
  /**
   * 先二次确认，再执行导出；返回是否用户确认并完成导出（取消为 false）。
   */
  function runExportWithConfirm(options: ExportConfirmOptions): Promise<boolean> {
    if (exportConfirmUi.submitting) {
      return Promise.resolve(false)
    }
    if (pending) {
      pending.resolve(false)
      pending = null
    }
    const label = resolveExportLabel(options.dataLabel)
    const matchedCount = options.matchedCount
    exportConfirmUi.title = options.title ?? '确认导出'
    exportConfirmUi.label = label
    exportConfirmUi.meta =
      matchedCount != null ? `当前筛选 ${formatCount(matchedCount)} 条` : '将按当前筛选条件生成文件'
    exportConfirmUi.extra = options.content?.trim() ?? ''
    exportConfirmUi.limitHint = options.maxRows
      ? buildExportLimitHint(matchedCount, options.maxRows)
      : ''
    exportConfirmUi.checkboxLabel = options.checkbox?.label ?? ''
    exportConfirmUi.checked = options.checkbox?.defaultChecked ?? false
    exportConfirmUi.okText = options.okText ?? '确定导出'
    exportConfirmUi.submitting = false
    exportConfirmUi.open = true

    return new Promise((resolve) => {
      pending = { options, resolve }
    })
  }

  return { runExportWithConfirm, EXPORT_DATA_LABELS }
}
